import React, { useState, useCallback } from "react";
import {
  Popover,
  Badge,
  Button,
  Typography,
  Space,
  Spin,
  Empty,
  Divider,
  Tooltip,
} from "antd";
import {
  BellOutlined,
  CheckOutlined,
  DeleteOutlined,
  CloseOutlined,
  CalendarOutlined,
  DollarCircleOutlined,
  AlertOutlined,
  BellFilled,
  CustomerServiceOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useNotifications, TutorNotification } from "../../hooks/useNotifications";

const { Text, Title } = Typography;

const NOTIFICATION_ICONS: Record<string, React.ReactNode> = {
  LESSONS_UNMARKED:      <CalendarOutlined style={{ color: "#f59e0b" }} />,
  LESSONS_UNMARKED_3DAY: <AlertOutlined    style={{ color: "#ef4444" }} />,
  INVOICE_UNPAID_3DAY:   <DollarCircleOutlined style={{ color: "#f59e0b" }} />,
  INVOICE_UNPAID_7DAY:   <DollarCircleOutlined style={{ color: "#ef4444" }} />,
  INVOICES_TO_SEND:      <DollarCircleOutlined style={{ color: "#6366f1" }} />,
  SUPPORT_REPLY:         <CustomerServiceOutlined style={{ color: "#6366f1" }} />,
  SUPPORT_RESOLVED:      <CheckCircleOutlined style={{ color: "#16a34a" }} />,
};

function timeAgo(isoString: string): string {
  const seconds = Math.floor((Date.now() - new Date(isoString).getTime()) / 1000);
  if (seconds < 60)  return "just now";
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  return `${Math.floor(seconds / 86400)}d ago`;
}

interface NotificationItemProps {
  item: TutorNotification;
  onRead: (key: string) => void;
  onDismiss: (key: string) => void;
  onNavigate: (link: string) => void;
}

const NotificationItem: React.FC<NotificationItemProps> = ({
  item,
  onRead,
  onDismiss,
  onNavigate,
}) => {
  const isUnread = !item.read;

  const handleClick = () => {
    if (isUnread) onRead(item.dedupeKey);
    onNavigate(item.link);
  };

  return (
    <div
      onClick={handleClick}
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: 10,
        padding: "10px 14px",
        cursor: "pointer",
        background: isUnread ? "#f0f4ff" : "transparent",
        borderRadius: 6,
        transition: "background 0.15s",
        position: "relative",
      }}
      onMouseEnter={(e) => (e.currentTarget.style.background = isUnread ? "#e0e8ff" : "#f9fafb")}
      onMouseLeave={(e) => (e.currentTarget.style.background = isUnread ? "#f0f4ff" : "transparent")}
    >
      {/* Type icon */}
      <div style={{ marginTop: 2, flexShrink: 0, fontSize: 16 }}>
        {NOTIFICATION_ICONS[item.type] || <BellOutlined style={{ color: "#6b7280" }} />}
      </div>

      {/* Content */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <Text
          strong={isUnread}
          style={{
            display: "block",
            fontSize: 13,
            color: isUnread ? "#111827" : "#374151",
            lineHeight: "1.35",
            whiteSpace: "normal",
          }}
        >
          {item.title}
        </Text>
        <Text
          style={{
            display: "block",
            fontSize: 12,
            color: "#6b7280",
            lineHeight: "1.4",
            whiteSpace: "normal",
            marginTop: 2,
          }}
        >
          {item.body}
        </Text>
        <Text style={{ fontSize: 11, color: "#9ca3af", marginTop: 4, display: "block" }}>
          {timeAgo(item.createdAt)}
        </Text>
      </div>

      {/* Dismiss button */}
      <Tooltip title="Dismiss">
        <Button
          type="text"
          size="small"
          icon={<CloseOutlined />}
          style={{
            color: "#9ca3af",
            flexShrink: 0,
            marginTop: -2,
            marginRight: -4,
            opacity: 0.6,
          }}
          onClick={(e) => {
            e.stopPropagation();
            onDismiss(item.dedupeKey);
          }}
        />
      </Tooltip>

      {/* Unread dot */}
      {isUnread && (
        <div
          style={{
            position: "absolute",
            right: 14,
            bottom: 10,
            width: 6,
            height: 6,
            borderRadius: "50%",
            background: "#6366f1",
          }}
        />
      )}
    </div>
  );
};

export const NotificationsPanel: React.FC = () => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const {
    notifications,
    unreadCount,
    loading,
    markRead,
    markAllRead,
    dismiss,
    clearAll,
  } = useNotifications();

  const handleOpen = useCallback(
    async (visible: boolean) => {
      setOpen(visible);
      if (visible && unreadCount > 0) {
        await markAllRead();
      }
    },
    [unreadCount, markAllRead]
  );

  const handleNavigate = useCallback(
    (link: string) => {
      setOpen(false);
      navigate(link);
    },
    [navigate]
  );

  const panelContent = (
    <div style={{ width: 340 }}>
      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "12px 14px 8px",
          borderBottom: "1px solid #f3f4f6",
        }}
      >
        <Title level={5} style={{ margin: 0, fontSize: 14 }}>
          Notifications
        </Title>
        <Space size={4}>
          {unreadCount > 0 && (
            <Tooltip title="Mark all read">
              <Button
                type="text"
                size="small"
                icon={<CheckOutlined />}
                style={{ color: "#6b7280" }}
                onClick={markAllRead}
              />
            </Tooltip>
          )}
          {notifications.length > 0 && (
            <Tooltip title="Clear all">
              <Button
                type="text"
                size="small"
                icon={<DeleteOutlined />}
                style={{ color: "#6b7280" }}
                onClick={clearAll}
              />
            </Tooltip>
          )}
        </Space>
      </div>

      {/* Body */}
      <div style={{ maxHeight: 400, overflowY: "auto" }}>
        {loading ? (
          <div style={{ textAlign: "center", padding: "32px 0" }}>
            <Spin size="small" />
          </div>
        ) : notifications.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="All caught up!"
            style={{ padding: "32px 0" }}
          />
        ) : (
          notifications.map((n, i) => (
            <React.Fragment key={n.dedupeKey}>
              <NotificationItem
                item={n}
                onRead={markRead}
                onDismiss={dismiss}
                onNavigate={handleNavigate}
              />
              {i < notifications.length - 1 && (
                <Divider style={{ margin: "0 14px", borderColor: "#f3f4f6" }} />
              )}
            </React.Fragment>
          ))
        )}
      </div>
    </div>
  );

  return (
    <Popover
      content={panelContent}
      trigger="click"
      open={open}
      onOpenChange={handleOpen}
      placement="bottomRight"
      arrow={false}
      overlayInnerStyle={{ padding: 0, borderRadius: 10 }}
      overlayStyle={{ zIndex: 1050 }}
    >
      <Badge count={unreadCount} size="small" overflowCount={9}>
        <Button
          type="text"
          aria-label="Notifications"
          icon={unreadCount > 0 ? <BellFilled /> : <BellOutlined />}
          style={{
            color: unreadCount > 0 ? "#6366f1" : "#6b7280",
            fontSize: "16px",
          }}
        />
      </Badge>
    </Popover>
  );
};
